import { keyframes } from "@emotion/react";

import { ThemeUIStyleObject, getThemeValue } from "@theme/components";

const focusAnimation = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-5px", opacity: 1 },
});

const focusAnimationStyleSelectorLabel = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-4px", opacity: 1 },
});

export const label: ThemeUIStyleObject = {
  fontFamily: "textSans",
  fontSize: "fs12",
  lineHeight: "lh100",
};

export const checkbox: ThemeUIStyleObject = {
  primary: {
    "input ~ &:hover": {
      borderColor: "borderPrimary",
    },
    "input:disabled ~ &": {
      bg: "interactiveNeutralFaded",
    },
    "input:disabled ~ &:hover": {
      borderColor: "borderLevel2",
    },
    "input:checked ~ &": {
      borderColor: "transparent",
      bg: "goldPrimary",
      svg: { display: "block", color: "white" },
    },
    "input:checked ~ &:hover": {
      bg: "backgroundBrandDarkStatic",
    },
    "input:checked:disabled ~ &": {
      borderColor: "borderLevel2",
      bg: "interactiveNeutralFaded",
      svg: { color: "textLevel3" },
    },
    ".keyboard-user input:focus ~ &": {
      ":before": {
        content: "''",
        position: "absolute",
        animation: `${focusAnimation} 0.3s ease-in-out forwards`,
        borderRadius: "xs",
        borderWidth: "thick",
        borderStyle: "solid",
        borderColor: "borderBrand",
      },
    },
  },
};

export const fieldLabel: ThemeUIStyleObject = {
  width: "auto",
  transition: ".15s cubic-bezier(0.4, 0, 0.2, 1)",
  pointerEvents: "none",
  variant: "text.utilityLabel01Regular",
  textTransform: "uppercase",
};

export const field: ThemeUIStyleObject = {
  pt: 0,
  pb: 0,
  border: 0,
  borderRadius: "none",
  bg: "transparent",
  height: "full",
  color: "textPrimary",
  variant: "text.sansBody02Regular",
  ":focus": {
    outline: "none",
  },
  ":disabled": {
    color: "textLevel2",
  },
};

export const fieldBorder: ThemeUIStyleObject = {
  // After pseudo element is used since border changes spacing on
  // inside content and outline cannot use border radius
  ":after": {
    content: "''",
    position: "absolute",
    borderRadius: "xs",
    borderWidth: "light",
    borderStyle: "solid",
    borderColor: "borderLevel2",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: "none",
  },
  "&:hover": {
    ":after": {
      borderColor: "borderPrimary",
    },
  },
  ".keyboard-user &:focus-within, &:focus-within": {
    ":after": {
      borderWidth: "thick",
      borderColor: "borderBrand",
    },
  },
};

export const icon: ThemeUIStyleObject = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: "iconSizeBase",
  height: "iconSizeBase",
  pointerEvents: "none",
  color: "textPrimary",
};
export const iconButton: ThemeUIStyleObject = {
  variant: "forms.icon",
  width: 35,
  height: 35,
  pointerEvents: "all",
};

export const leadingIcon: ThemeUIStyleObject = {
  variant: "forms.icon",
  left: "s400",
};
export const leadingButton: ThemeUIStyleObject = {
  variant: "forms.iconButton",
  left: "s400",
};

export const trailingIcon: ThemeUIStyleObject = {
  variant: "forms.icon",
  right: "s400",
};

export const trailingButton: ThemeUIStyleObject = {
  variant: "forms.iconButton",
  right: "s400",
};

export const halfPillInput: ThemeUIStyleObject = {
  variant: "forms.fieldBorder",
  position: "relative",

  // This block here is because we have a light theme over a dark background.
  // Forcing the element to be a white background which deviates a little bit.
  bg: "white",
  borderTopStyle: "solid",
  borderTopLeftRadius: "rounded",
  borderBottomLeftRadius: "rounded",
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  borderWidth: 0,
  // End block

  // Field Border styles for this unique rounded input.
  "&:after": {
    borderTopStyle: "solid",
    borderTopLeftRadius: "rounded",
    borderBottomLeftRadius: "rounded",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRightWidth: 0,
    borderTopWidth: "light",
    borderLeftWidth: "light",
    borderBottomWidth: "light",
    // This style is to have light theme over a dark background.
    borderColor: "borderLevel2",
  },
  ".keyboard-user &:focus-within, &:focus-within": {
    ":after": {
      borderRightWidth: 0,
      borderTopWidth: "thick",
      borderLeftWidth: "thick",
      borderBottomWidth: "thick",
      borderColor: "borderBrand",
    },
  },
};

export const primarySwitch: ThemeUIStyleObject = {
  "input ~ &": {
    bg: "interactiveNeutralFaded",
  },
  "input ~ & svg": {
    display: "none",
  },
  "input ~ &:hover": {
    bg: "interactiveNeutralHighlighted",
  },
  "input:checked ~ &:hover": {
    bg: "backgroundBrandDarkStatic",
  },
  "input:checked ~ &": {
    bg: "backgroundBrand",
  },
  "input:checked ~ & svg": {
    display: "block",
    color: "textBrand",
  },
  "input:disabled ~ &, input:disabled ~ &:hover": {
    bg: "interactiveNeutralFaded",
  },
  "input:disabled ~ & > div": {
    bg: "grey300",
  },
  "input:disabled ~ & ~ span": {
    color: "textLevel3",
  },
  "input:checked:disabled ~ & svg": {
    color: "textPrimary",
  },
  ".keyboard-user input:focus ~ &": {
    bg: "interactiveNeutralHighlighted",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimation} 0.3s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "3xl",
    },
  },
  ".keyboard-user input:checked:focus ~ &": {
    bg: "backgroundBrand",
  },
};

export const styleSelectorLabel: ThemeUIStyleObject = {
  cursor: "pointer",
  'input[type="radio"]': {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
  },
  ".keyboard-user &:focus-within": {
    outline: "none",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimationStyleSelectorLabel} 0.3s ease-in-out forwards`,
      border: (t) => `2px solid ${getThemeValue(t, "colors.goldPrimary")}`,
    },
  },
};

export const navCategoryLabel: ThemeUIStyleObject = {
  cursor: "pointer",
  width: 130,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textTransform: "capitalize",
  fontWeight: "semibold",
  position: "relative",
  borderRadius: "none",
  transition: "color 0.2s ease-in-out",
  "&:hover": {
    "*": { color: "black" },
  },
  'input[type="radio"]': {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
  },
  ".keyboard-user &:focus-within": {
    outline: "none",
    ":before": {
      content: "''",
      position: "absolute",
      // borderRadius: "rounded",
      animation: `${focusAnimationStyleSelectorLabel} 0.3s ease-in-out forwards`,
      border: (t) => `2px solid ${getThemeValue(t, "colors.goldPrimary")}`,
    },
  },
};

export const radio: ThemeUIStyleObject = {
  minWidth: "min-content",
  // Show checked version of radio button
  "input:checked ~ &::before": {
    display: "block",
  },
  "input:checked ~ &": {
    borderColor: "borderPrimary",
  },
  "input:disabled ~ &": {
    cursor: "not-allowed",
  },
  // Hover
  "input ~ &:hover": {
    borderColor: "borderPrimary",
    bg: "interactiveNeutralDim",
  },
  // Checked + Hover
  "input:checked ~ &:hover": {
    bg: "transparent",
    borderColor: "borderLevel2",
    ":before": {
      bg: "textBrandLevel2",
    },
  },
  // Disabled
  "input:disabled ~ &, input:disabled ~ &:hover": {
    borderColor: "borderLevel2",
    bg: "interactiveNeutralFaded",
  },
  // Checked + Disabled
  "input:checked:disabled ~ &": {
    ":before": {
      bg: "textLevel3",
    },
  },
  ".keyboard-user input:focus ~ &": {
    ":after": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimation} 0.3s ease-in-out forwards`,
      borderRadius: "full",
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
    },
  },
};
